import { lazy, useMemo, useRef } from "react"
import type { FC, LazyExoticComponent, ReactElement, RefObject } from "react"

import { ErrorBoundary } from "react-error-boundary"

import { ErrorHint, Loadable, Spinner } from "@/3514/components"
import { withNetworkGuard } from "@/3514/guards/hoc"
import { type TScreenBreakpoints, useIsFakeParticipant, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { VIEW_AS_BANNER_DOME_ELEMENT_ID } from "@/layouts"
import { WiserLogo } from "@/svgs"

const classes = {
    container: (): string => "flex flex-col",
    errorHint: (): string => "!mt-[10%]",
    header: ({ isDesktop }: { isDesktop: boolean }): string =>
        buildCssClassName("flex m-auto max-w-[540px]", "shadow-sm", isDesktop || "overflow-hidden w-full"),
    spinner: (): string => "mt-[50px] gap-y-[5px]",
    spinnerLogo: (): string => "animate-bounce"
} as const

const Container: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> =>
        import("./participant.chapter.container").then(({ ParticipantChapterContainer: C }): { default: FC } => ({
            default: C
        }))
)

/**
 * Participant' Chapter page.
 * Design: {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=13200-158580&m=dev}
 *
 * @return ReactElement
 */
const Page: FC = (): ReactElement => {
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isFakeParticipant: boolean = useIsFakeParticipant()

    const bannerElement: RefObject<HTMLElement> = useRef<HTMLElement>(
        document.getElementById(VIEW_AS_BANNER_DOME_ELEMENT_ID)
    )

    // Memoized so <Loadable /> memoization holds across this page' re-renders
    // (useNetworkState/useScreenBreakpoints fire often on mobile) and the chapter subtree is
    // never rebuilt from scratch mid-session.
    // NB: must stay inside the component. Hoisted to module scope it dereferences <Spinner />
    // while the "@/3514/components" barrel is still initializing, which throws a TDZ
    // ReferenceError in the production bundle (the dev server evaluates modules in a different
    // order and does not reproduce it).
    const loadingFallback: ReactElement = useMemo(
        (): ReactElement => (
            <Spinner
                renderLogo={(): ReactElement => <WiserLogo className={classes.spinnerLogo()} width={35} height={35} />}
                wrapperClassName={classes.spinner()}
            />
        ),
        []
    )

    return (
        <div
            className={classes.container()}
            {...(Boolean(isFakeParticipant && Boolean(breakpoints.xs || breakpoints.sm)) && {
                style: { marginTop: `${bannerElement.current?.clientHeight}px` }
            })}
        >
            <div className={classes.header({ isDesktop: breakpoints.xl || breakpoints.xxl })} />

            <ErrorBoundary fallback={<ErrorHint isVisible containerClassName={classes.errorHint()} />}>
                <Loadable component={Container} loadingFallback={loadingFallback} withEnforcedSuspense />
            </ErrorBoundary>
        </div>
    )
}

Page.displayName = "ParticipantChapterPage"

const NetworkGuardedPage: FC = withNetworkGuard(Page)

export { NetworkGuardedPage as ParticipantChapterPage }
